import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TenantPayments } from '../../api/account/accountApiTypes';
import {
    AdminProduct,
    useDeleteProductMutation,
    useGetProductsQuery,
} from '../../api/group/groupAdminApiSlice';
import { useIsMaster } from '../../common/useIsMaster';
import HardDeleteDialog from '../common/HardDeleteDialog';
import CustomButton from '../../components/CustomButton';
import RowActionsMenu from '../../components/RowActionsMenu';
import { roomUseCasesOf } from '../../utils/roomUseCases';
import TableCard, { TableHeaderRow, tableCellClass } from '../../components/TableCard';
import { getCurrentLang } from '../../i18n/i18n';
import { formatCurrency } from '../../utils';
import ProductFormDialog from './components/ProductFormDialog';
import ProductRetireDialog from './components/ProductRetireDialog';
import {
    LISTED_PRODUCT_USE_CASES,
    isRoomProduct,
    productInSectionFamily,
} from './serviceConfigurationRules';

type Editing =
    | { mode: 'create' }
    | { mode: 'edit', product: AdminProduct };

type ProductsSectionProps = {
    /** Use cases the tenant offers — the section renders only when at least
     *  one has products listed here (`LISTED_PRODUCT_USE_CASES`). */
    services: string[];
    /** Masters on a foreign tenant read only; the buttons disappear. */
    readOnly: boolean;
    /** The tenant's payment configuration — a free room use case's prices
     *  are locked in the room price dialog. */
    payments: TenantPayments | null;
    /** The `?product=` deep-link id (contract with the object/product detail
     *  surface): a product id, or — pre-W1.2a links — the mirror group's id.
     *  Resolved once the list is loaded; a miss is stated, never silent. */
    deepLinkProductId?: string | null;
};

/**
 * The products themselves — create and edit
 * (docs/plans/service-configuration-area.md, product-form step). This is the
 * missing half of the station wizard's own flow: its link step points here to
 * "create a Product in Services first".
 *
 * Since #2923 that includes ROOM products: a type like "1-Zimmer/Küche/Bad"
 * that apartments are then assigned to from their own page. The objects
 * themselves — address, opening hours, photos — are still created on the
 * asset pages; a room product is none of that.
 *
 * Prices: the station-rental family's are edited by the tariff editor in the
 * price structure below. A room product's are edited here, in the room price
 * dialog. It is not opened on its own after a room product is created (F, PO
 * 2026-10-11: a save closes the form, nothing opens unasked); instead a room
 * product without prices says so in its row, next to the "Preise" action —
 * such a product would price every object assigned to it with nothing.
 */
const ProductsSection = ({
    services, readOnly, payments, deepLinkProductId,
}: ProductsSectionProps) => {
    const { t } = useTranslation();
    const { data: products, isLoading, isError } = useGetProductsQuery();
    const [editing, setEditing] = useState<Editing | null>(null);
    // Retiring is the tenant's own act, so it is offered to whoever may edit
    // products — no extra gate (PO, 2026-09-16: "wenn es ein Archivieren ist,
    // dann kann das auch der Kunde tun. Wenn es ein echtes Löschen ist, dann
    // tun wir das"). The master role is reserved for the hard delete (#2636).
    const [retiring, setRetiring] = useState<AdminProduct | null>(null);
    // The hard delete is the master's (#2636) — offered to a master even on a
    // tenant they otherwise view read-only, because that is exactly where a
    // customer's configuration mistake gets cleaned up.
    const isMaster = useIsMaster();
    const [deleting, setDeleting] = useState<AdminProduct | null>(null);
    const [deleteProduct] = useDeleteProductMutation();
    const [deepLinkMiss, setDeepLinkMiss] = useState(false);
    // The deep link opens the form once — closing it must not re-open it on
    // the next render.
    const deepLinkConsumed = useRef(false);

    /**
     * Which products are listed, and which use cases a product may be created
     * for here — one set since #2923, when room products became creatable.
     * Retiring is decided per row (`isRoomProduct`), see the retire button.
     *
     * Until #2857 both were `TARIFF_USE_CASES`, and because that set is
     * disjoint from the category-aware one, a tenant offering only
     * `apartment`/`venue` — the category layer's own target — fell straight
     * through the `return null` below. The whole section never rendered, so
     * the dialog carrying the category picker never rendered either, so no
     * product could be filed under a category, so every category stayed empty
     * and its home tile led nowhere.
     */
    const listedUseCases = useMemo(
        () => services.filter((service) => LISTED_PRODUCT_USE_CASES.has(service.toLowerCase())),
        [services],
    );

    const rows = useMemo(
        () => (products ?? []).filter((product) => productInSectionFamily(product, listedUseCases)),
        [products, listedUseCases],
    );

    useEffect(() => {
        if (!deepLinkProductId || deepLinkConsumed.current || !products) return;
        // Read-only viewers (masters on a foreign tenant) get the list, not an
        // editable form the backend would actually accept writes from.
        if (readOnly) return;
        deepLinkConsumed.current = true;
        // Both id eras: the contract carried the group id before W1.2a.
        const match = products.find(
            (product) => product.id === deepLinkProductId
                || product.groupId === deepLinkProductId,
        );
        if (match) {
            setEditing({ mode: 'edit', product: match });
        } else {
            // Fail loud: a link to a product that does not resolve must not
            // silently land on an unrelated list.
            setDeepLinkMiss(true);
        }
    }, [deepLinkProductId, products, readOnly]);

    if (listedUseCases.length === 0) return null;

    const labelForUseCase = (product: AdminProduct): string => {
        const type = product.useCase
            ?? product.compartmentTypes[0]?.toLowerCase()
            ?? '';
        return t(`service_type_selector.${type}`, { defaultValue: type });
    };

    return (
        <section className={'bg-text-contrast rounded-[5px] shadow-sm p-6'}>
            <h2 className={'text-lg font-semibold text-foreground mb-1'}>
                {t('services.products.title')}
            </h2>
            <p className={'text-sm text-foreground/60 mb-5'}>
                {t('services.products.description')}
            </p>

            {/* Who pays is not a price: a covered booking keeps its full price
                and only the payer changes (ADR-0008 amendment 2026-10-05), so
                it is decided on the organization, room by room. Said here
                because this is where someone looks for "why do members pay
                nothing?" (PO, 2026-10-11). */}
            {roomUseCasesOf(services).length > 0 && (
                <p className={'text-sm text-foreground/60 mb-5'}>
                    {t('services.products.member_coverage_hint')}
                </p>
            )}

            {deepLinkMiss && (
                <div className={'rounded-[5px] border border-foreground/15 bg-foreground/5'
                    + ' px-4 py-3 text-sm text-foreground/70 mb-4'}
                >
                    {t('services.products.deep_link_not_found')}
                </div>
            )}

            {isLoading && (
                <div className={'flex justify-center py-10'}>
                    <div className={'animate-spin rounded-full h-10 w-10 border-b-2 border-primary'} />
                </div>
            )}
            {!isLoading && isError && (
                // An error is never rendered as an empty state.
                <p className={'text-sm text-foreground/60'}>
                    {t('services.products.load_error')}
                </p>
            )}
            {!isLoading && !isError && (
                <div className={'flex flex-col gap-4'}>
                    {rows.length === 0 ? (
                        <p className={'text-sm text-foreground/40 italic'}>
                            {t('services.products.no_products')}
                        </p>
                    ) : (
                        <TableCard>
                            <TableHeaderRow
                                labels={[
                                    t('services.products.columns.name'),
                                    t('services.products.columns.use_case'),
                                    t('services.products.columns.deposit'),
                                    '',
                                ]}
                            />
                            <tbody>
                                {rows.map((product) => (
                                    <tr
                                        key={product.id}
                                        className={'border-b border-foreground/5 last:border-b-0'}
                                    >
                                        <td className={tableCellClass}>
                                            {product.name ?? (
                                                <span className={'italic text-foreground/40'}>
                                                    {t('common.missing')}
                                                </span>
                                            )}
                                            {/* Only a list that came back empty says "none": an older
                                                backend omits the field, and unknown is not "no prices". */}
                                            {isRoomProduct(product) && Array.isArray(product.priceScales)
                                                && product.priceScales.length === 0 && (
                                                <div className={'text-xs text-foreground/60'}>
                                                    {t('services.products.no_prices')}
                                                </div>
                                            )}
                                        </td>
                                        <td className={tableCellClass}>{labelForUseCase(product)}</td>
                                        <td className={tableCellClass}>
                                            {product.depositAmount != null
                                                ? formatCurrency(product.depositAmount, getCurrentLang())
                                                : t('services.deposits.no_deposit')}
                                        </td>
                                        <td className={`${tableCellClass} text-right`}>
                                            {/* Row actions (design-decisions.md › Zeilenaktionen in
                                                Tabellen): every one changes something, so all sit in
                                                the row's menu, the hard delete last. */}
                                            <RowActionsMenu
                                                rowName={product.name || t('common.missing')}
                                                items={[
                                                    {
                                                        id: 'edit',
                                                        label: t('common.edit'),
                                                        onClick: () => setEditing({ mode: 'edit', product }),
                                                        hidden: readOnly,
                                                    },
                                                    {
                                                        // Retiring a room product stays gated: archiving a
                                                        // live room's product drops its price back to the
                                                        // legacy Group source and loses its category.
                                                        id: 'retire',
                                                        label: t('services.products.retire.action'),
                                                        onClick: () => setRetiring(product),
                                                        hidden: readOnly || isRoomProduct(product),
                                                    },
                                                ]}
                                                destructiveItems={[{
                                                    id: 'delete',
                                                    label: t('services.products.delete.action'),
                                                    onClick: () => setDeleting(product),
                                                    hidden: !isMaster,
                                                }]}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </TableCard>
                    )}

                    {!readOnly && (
                        <div>
                            <CustomButton
                                title={t('services.products.new_product')}
                                onClick={() => setEditing({ mode: 'create' })}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Mounted per product so the field state seeds from the row. */}
            {editing && (
                <ProductFormDialog
                    key={editing.mode === 'edit' ? editing.product.id : 'new'}
                    product={editing.mode === 'edit' ? editing.product : null}
                    // Creation only — the dialog hides this select when editing
                    // and reads the product's own use case instead.
                    useCaseOptions={listedUseCases.map((service) => service.toLowerCase())}
                    onClose={() => setEditing(null)}
                />
            )}

            {/* Keyed per product for the same reason: the dialog holds the
                refusal from the last attempt, which must not survive into a
                different product's confirmation. */}
            {retiring && (
                <ProductRetireDialog
                    key={retiring.id}
                    product={retiring}
                    onClose={() => setRetiring(null)}
                />
            )}

            {deleting && (
                <HardDeleteDialog
                    key={deleting.id}
                    name={deleting.name ?? t('common.missing')}
                    i18nPrefix={'services.products.delete'}
                    run={() => deleteProduct({ productId: deleting.id }).unwrap()}
                    onClose={() => setDeleting(null)}
                />
            )}
        </section>
    );
};

export default ProductsSection;
